/**
 * GridSelectionHelper – encapsulates drag-to-select logic for ag-grid cells.
 * Mirrors Angular GridSelectionHelper but as a plain class (no DI).
 */
import type { GridApi } from 'ag-grid-community';
import { TIME_PERIOD, TIME_QUARTER } from '../constants';

export interface CellInfo {
  rowIndex: number;
  colIndex: number;
  colId: string;
  field: string;
}

export interface SelectionState {
  selectedCells: Set<string>;
  selectedIndices: Map<number, Set<number>>;
  selectedCellsSum: number;
  selectedCellsCount: number;
  isDragging: boolean;
  startCell: CellInfo | null;
  endCell: CellInfo | null;
  columnFieldsSnapshot: string[] | null;
  selectionDirty: boolean;
}

export class GridSelectionHelper {
  private readonly state: SelectionState;
  private gridApi: GridApi | null = null;
  private onSelectionChanged?: (cellsCount: number, sum: number) => void;

  constructor() {
    this.state = this.createInitialState();
  }

  private createInitialState(): SelectionState {
    return {
      selectedCells: new Set<string>(),
      selectedIndices: new Map<number, Set<number>>(),
      selectedCellsSum: 0,
      selectedCellsCount: 0,
      isDragging: false,
      startCell: null,
      endCell: null,
      columnFieldsSnapshot: null,
      selectionDirty: false,
    };
  }

  setGridApi(gridApi: GridApi): void {
    this.gridApi = gridApi;
  }

  setSelectionChangeCallback(callback: (cellsCount: number, sum: number) => void): void {
    this.onSelectionChanged = callback;
  }

  getState(): Readonly<SelectionState> {
    return this.state;
  }

  // ── Mouse events ─────────────────────────────────────────────

  handleCellMouseDown(event: { event: MouseEvent; rowIndex: number; column: { getColId(): string; getColDef(): { field?: string; colId?: string } } }): boolean {
    if (event.event.button !== 0 || !this.gridApi) return false;

    // Now we can safely prevent default since single-click won't trigger editing
    // (ag-grid is configured for double-click editing like Angular)
    event.event.preventDefault();
    event.event.stopPropagation();

    const cellInfo = this.extractCellInfo(event);
    if (!cellInfo) return false;

    this.state.isDragging = true;
    this.state.startCell = cellInfo;
    this.state.endCell = cellInfo;
    this.state.columnFieldsSnapshot =
      (this.gridApi.getAllDisplayedColumns() ?? []).map((c) => {
        const def = c.getColDef();
        return def.field ?? def.colId ?? c.getColId();
      });

    this.clearSelectionState();
    this.updateSelectedRange();
    this.state.selectionDirty = true;
    return true;
  }

  handleCellMouseOver(event: { rowIndex: number; column: { getColId(): string; getColDef(): { field?: string; colId?: string } } }): boolean {
    if (!this.state.isDragging || !this.state.startCell || !this.gridApi) return false;

    const def = event.column.getColDef();
    const hoverField = def.field ?? def.colId ?? event.column.getColId();
    if (!hoverField) return false;

    const fields = this.state.columnFieldsSnapshot ?? [];
    const hoverIdx = fields.indexOf(hoverField);
    if (hoverIdx === -1) return false;

    this.state.endCell = {
      rowIndex: event.rowIndex,
      colIndex: hoverIdx,
      colId: event.column.getColId(),
      field: hoverField,
    };

    this.updateSelectedRange();
    this.state.selectionDirty = true;
    return true;
  }

  handleCellMouseUp(): boolean {
    if (!this.state.isDragging || !this.gridApi) return false;
    this.state.isDragging = false;

    // Only trigger a full refresh when the user actually dragged across
    // multiple cells.  A single-cell "click" (start === end) should NOT
    // refresh – that would dismiss ag-grid tooltips and break double-click.
    const didDrag =
      this.state.startCell &&
      this.state.endCell &&
      (this.state.startCell.rowIndex !== this.state.endCell.rowIndex ||
        this.state.startCell.colIndex !== this.state.endCell.colIndex);

    if (this.state.selectionDirty) {
      requestAnimationFrame(() => {
        this.calculateSum();
        this.state.selectionDirty = false;
        if (didDrag) {
          requestAnimationFrame(() => {
            this.gridApi?.refreshCells({ suppressFlash: true });
          });
        }
      });
    }
    return true;
  }

  // ── Public helpers ───────────────────────────────────────────

  clearSelection(): void {
    this.clearSelectionState();
    this.onSelectionChanged?.(0, 0);
    requestAnimationFrame(() => {
      this.gridApi?.refreshCells({ suppressFlash: true });
    });
  }

  /**
   * Cancel ongoing drag selection (e.g., when double-click is detected).
   * This prevents the first click of a double-click from triggering drag selection.
   */
  cancelDrag(): void {
    this.state.isDragging = false;
    this.state.startCell = null;
    this.state.endCell = null;
  }

  createCellKey(rowIndex: number, column: { getColDef?(): { field?: string; colId?: string }; getColId?(): string; colDef?: { field?: string; colId?: string }; field?: string; colId?: string }): string {
    const id =
      column.getColDef?.()?.field ??
      column.getColDef?.()?.colId ??
      column.colDef?.field ??
      column.colDef?.colId ??
      column.field ??
      column.colId ??
      column.getColId?.() ??
      'unknown';
    return `${rowIndex}_${id}`;
  }

  // ── Private ──────────────────────────────────────────────────

  private extractCellInfo(event: { rowIndex: number; column: { getColId(): string; getColDef(): { field?: string; colId?: string } } }): CellInfo | null {
    if (!this.gridApi) return null;
    const allCols = this.gridApi.getAllDisplayedColumns() ?? [];
    const colIndex = allCols.findIndex((c) => c.getColId() === event.column.getColId());
    if (colIndex === -1) return null;

    const def = event.column.getColDef();
    const field = def.field ?? def.colId ?? event.column.getColId();

    return { rowIndex: event.rowIndex, colIndex, colId: event.column.getColId(), field };
  }

  private clearSelectionState(): void {
    this.state.selectedCells.clear();
    this.state.selectedIndices.clear();
    this.state.selectedCellsSum = 0;
    this.state.selectedCellsCount = 0;
  }

  private updateSelectedRange(): void {
    const { startCell, endCell, columnFieldsSnapshot } = this.state;
    if (!startCell || !endCell || !this.gridApi || !columnFieldsSnapshot) return;

    this.state.selectedCells.clear();
    this.state.selectedIndices.clear();

    const startColIdx = columnFieldsSnapshot.indexOf(startCell.field);
    const endColIdx = columnFieldsSnapshot.indexOf(endCell.field);
    if (startColIdx === -1 || endColIdx === -1) return;

    const [minRow, maxRow] = [Math.min(startCell.rowIndex, endCell.rowIndex), Math.max(startCell.rowIndex, endCell.rowIndex)];
    const [minCol, maxCol] = [Math.min(startColIdx, endColIdx), Math.max(startColIdx, endColIdx)];

    for (let row = minRow; row <= maxRow; row++) {
      const colIndices = new Set<number>();
      this.state.selectedIndices.set(row, colIndices);
      for (let col = minCol; col <= maxCol; col++) {
        const field = columnFieldsSnapshot[col];
        if (field) {
          colIndices.add(col);
          this.state.selectedCells.add(`${row}_${field}`);
        }
      }
    }

    requestAnimationFrame(() => {
      this.gridApi?.refreshCells({ suppressFlash: true });
    });
  }

  private calculateSum(): void {
    if (this.state.selectedCells.size === 0) {
      this.state.selectedCellsSum = 0;
      this.state.selectedCellsCount = 0;
      this.onSelectionChanged?.(0, 0);
      return;
    }

    let sum = 0;
    const periodIds = new Set<string>();
    const quarterIds = new Set<string>();
    let weekCellCount = 0;

    for (const cellKey of this.state.selectedCells) {
      const parsed = this.parseCellKey(cellKey);
      if (!parsed || !this.gridApi) continue;

      const rowNode = this.gridApi.getDisplayedRowAtIndex(parsed.rowIndex);
      if (!rowNode?.data) continue;

      const timeLevel = rowNode.data.timeLevel as string;

      if (this.categorizeCellForCount(timeLevel, parsed.fieldName, periodIds, quarterIds)) {
        weekCellCount++;
      }

      const cellValue = this.resolveCellValue(rowNode, parsed.fieldName, timeLevel);
      sum += this.extractNumericValue(cellValue, timeLevel);
    }

    const count = periodIds.size + quarterIds.size + weekCellCount;
    this.state.selectedCellsSum = sum;
    this.state.selectedCellsCount = count;
    this.onSelectionChanged?.(count, sum);
  }

  private parseCellKey(cellKey: string): { rowIndex: number; fieldName: string } | null {
    const underscoreIdx = cellKey.indexOf('_');
    if (underscoreIdx === -1) return null;
    const rowIndex = Number.parseInt(cellKey.substring(0, underscoreIdx), 10);
    if (Number.isNaN(rowIndex)) return null;
    return { rowIndex, fieldName: cellKey.substring(underscoreIdx + 1) };
  }

  /** Returns true when the cell is a week-level cell (should be counted). */
  private categorizeCellForCount(
    timeLevel: string,
    fieldName: string,
    periodIds: Set<string>,
    quarterIds: Set<string>,
  ): boolean {
    const quarterMatch = /Q(\d+)/.exec(fieldName);
    const periodMatch = /P(\d+)/.exec(fieldName);

    if (timeLevel === TIME_PERIOD && periodMatch) {
      periodIds.add(`Q${quarterMatch?.[1] ?? 'X'}_P${periodMatch[1]}`);
      return false;
    }
    if (timeLevel === TIME_QUARTER && quarterMatch) {
      quarterIds.add(`Q${quarterMatch[1]}`);
      return false;
    }
    return timeLevel !== TIME_PERIOD && timeLevel !== TIME_QUARTER;
  }

  /** Resolves cell value from row data, falling back to valueGetter for computed rows. */
  private resolveCellValue(
    rowNode: { data: Record<string, unknown> },
    fieldName: string,
    timeLevel: string,
  ): unknown {
    const directValue = rowNode.data[fieldName];
    if (directValue != null) return directValue;
    if (timeLevel !== TIME_QUARTER && timeLevel !== TIME_PERIOD) return directValue;

    const column = this.gridApi?.getAllDisplayedColumns()?.find((col) => {
      const cd = col.getColDef();
      return cd.field === fieldName || cd.colId === fieldName || col.getColId() === fieldName;
    });
    if (!column) return directValue;

    const cd = column.getColDef();
    if (typeof cd.valueGetter !== 'function') return directValue;

    try {
      return cd.valueGetter({
        data: rowNode.data, node: rowNode, column, colDef: cd,
        api: this.gridApi, getValue: (f: string) => rowNode.data[f],
      } as unknown as Parameters<typeof cd.valueGetter>[0]);
    } catch {
      return directValue;
    }
  }

  private extractNumericValue(cellValue: unknown, timeLevel: string): number {
    if (cellValue === '.' || cellValue === '.0') return 0;
    if (cellValue == null || cellValue === '') return 0;

    let processed: unknown = cellValue;
    if ((timeLevel === TIME_QUARTER || timeLevel === TIME_PERIOD) && typeof cellValue === 'string' && cellValue.includes('(')) {
      const match = /^(\d+(?:\.\d+)?)/.exec(cellValue);
      processed = match ? match[1] : cellValue;
    }

    const num = Number(processed);
    return Number.isNaN(num) ? 0 : num;
  }
}
